/**
 * clubSessions — pure session-grouping for the Club "this morning" card.
 *
 * Groups many members' swims into the shared mornings the crew swam together,
 * from the CHEAP list-level WorkoutSummary alone (startDate + swimmingLocationType).
 * Start coordinates are NOT available here (they live in the firehose route
 * points), so v1 clusters by local day + location type + a start-time window,
 * never by GPS proximity.
 *
 * DERIVED, NEVER STORED. Offline-first: a member's swim can sync hours late and
 * change a session retroactively, so grouping is a pure function re-run on every
 * load. Callers key UI off `sessionId` tolerantly and expect membership to grow.
 *
 * All date math is LOCAL time, matching aggregates.ts / swimCalendar.ts.
 */
import type { WorkoutSummary } from "@contract/types";

export interface MemberSwim {
  memberId: string; // == creatorUserRecordID (the ingest-bound origin id)
  swim: WorkoutSummary;
}

export type ClubLocationType = "pool" | "openWater" | "unknown";

export interface ClubSession {
  /** Derived id `${localDayKey}|${locationType}|${anchorHHMM}`. May shift only if
   *  a swim with an EARLIER start arrives late for the same bucket; re-derived
   *  every call. Callers key UI off this tolerantly. */
  sessionId: string;
  /** ISO-8601 of the earliest start among entries (the session anchor). */
  startDate: string;
  locationType: ClubLocationType;
  /** All members' swims in this session, ascending by startDate. */
  entries: MemberSwim[];
}

export interface GroupOptions {
  /** A swim joins a session while its start is within this many minutes of the
   *  session anchor (earliest start). Default 90. */
  windowMinutes?: number;
}

/** Default: a swim joins a session while within this many minutes of the anchor.
 *  Exported (public default; also keeps `noUnusedLocals` happy until it's consumed). */
export const DEFAULT_WINDOW_MINUTES = 90;

/** Local-time "YYYY-MM-DD" for an ISO instant (matches swimCalendar's local-day rule). */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Normalize the cheap list-level location signal to a club bucket. */
export function locationOf(swim: WorkoutSummary): ClubLocationType {
  const t = swim.swimmingLocationType;
  if (t === "pool") return "pool";
  if (t === "openWater") return "openWater";
  return "unknown";
}

/** Local "HH:MM" for an ISO instant (used in the derived sessionId). */
export function anchorHHMM(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Bucket member-swims by (local day, location type). Key = `${dayKey}|${loc}`. */
export function bucketByDayAndLocation(swims: MemberSwim[]): Map<string, MemberSwim[]> {
  const buckets = new Map<string, MemberSwim[]>();
  for (const ms of swims) {
    const key = `${localDayKey(ms.swim.startDate)}|${locationOf(ms.swim)}`;
    const arr = buckets.get(key);
    if (arr) arr.push(ms);
    else buckets.set(key, [ms]);
  }
  return buckets;
}

/** Greedily cluster ONE bucket's swims into windowed sessions. A swim joins the
 *  current cluster while its start is within `windowMinutes` of that cluster's
 *  anchor (earliest start); otherwise it opens a new cluster. Input need not be
 *  sorted — clusters come back ascending by start. */
export function clusterByWindow(
  bucket: MemberSwim[],
  windowMinutes: number,
): MemberSwim[][] {
  const sorted = [...bucket].sort(
    (a, b) => Date.parse(a.swim.startDate) - Date.parse(b.swim.startDate),
  );
  const windowMs = windowMinutes * 60_000;
  const clusters: MemberSwim[][] = [];
  let current: MemberSwim[] = [];
  let anchorMs = 0;
  for (const ms of sorted) {
    const t = Date.parse(ms.swim.startDate);
    if (current.length === 0) {
      current = [ms];
      anchorMs = t;
    } else if (t - anchorMs <= windowMs) {
      current.push(ms);
    } else {
      clusters.push(current);
      current = [ms];
      anchorMs = t;
    }
  }
  if (current.length > 0) clusters.push(current);
  return clusters;
}

/** Group all members' swims into shared sessions, most-recent first. Pure and
 *  idempotent: re-run on every load; a late swim simply re-groups. */
export function groupSessions(swims: MemberSwim[], opts: GroupOptions = {}): ClubSession[] {
  const windowMinutes = opts.windowMinutes ?? DEFAULT_WINDOW_MINUTES;
  const session = (locationType: ClubLocationType, entries: MemberSwim[]): ClubSession => {
    const anchor = entries[0].swim.startDate; // entries ascending
    return { sessionId: `${localDayKey(anchor)}|${locationType}|${anchorHHMM(anchor)}`, startDate: anchor, locationType, entries };
  };
  // B-021: a swim with no location signal (a C5 swim with poolM null: no lengths, so no
  // pool length) is not its own morning. Sessions form from the swims that DO carry a
  // location; each no-location swim then joins the same day's session nearest in time
  // (inside the window when one is, else by date). Only a day with no located swim at all
  // groups its no-location swims among themselves, as before.
  const located = swims.filter((ms) => locationOf(ms.swim) !== "unknown");
  const unlocated = swims.filter((ms) => locationOf(ms.swim) === "unknown");
  const sessions: ClubSession[] = [];
  for (const [key, bucket] of bucketByDayAndLocation(located)) {
    const locationType = key.split("|")[1] as ClubLocationType;
    for (const cluster of clusterByWindow(bucket, windowMinutes)) sessions.push(session(locationType, cluster));
  }
  const leftover: MemberSwim[] = [];
  for (const ms of unlocated) {
    const t = Date.parse(ms.swim.startDate);
    const day = localDayKey(ms.swim.startDate);
    let best = -1;
    let bestGap = Infinity;
    sessions.forEach((s, i) => {
      if (localDayKey(s.startDate) !== day) return;
      const gap = Math.min(...s.entries.map((e) => Math.abs(Date.parse(e.swim.startDate) - t)));
      if (gap < bestGap) { bestGap = gap; best = i; }
    });
    if (best < 0) { leftover.push(ms); continue; }
    const entries = [...sessions[best].entries, ms].sort((a, b) => Date.parse(a.swim.startDate) - Date.parse(b.swim.startDate));
    sessions[best] = session(sessions[best].locationType, entries);
  }
  for (const bucket of bucketByDayAndLocation(leftover).values()) {
    for (const cluster of clusterByWindow(bucket, windowMinutes)) sessions.push(session("unknown", cluster));
  }
  sessions.sort((a, b) => Date.parse(b.startDate) - Date.parse(a.startDate));
  return sessions;
}

/** The most recent session ("this morning"), or null if there are none. Order-
 *  independent (does not assume the input is sorted). */
export function mostRecentSession(sessions: ClubSession[]): ClubSession | null {
  let best: ClubSession | null = null;
  for (const s of sessions) {
    if (best === null || Date.parse(s.startDate) > Date.parse(best.startDate)) best = s;
  }
  return best;
}
